import React from 'react';
import { Field, Spinner } from '../../components/ui';
import { fmtDate, fmtTime, money } from '../../utils/helpers';
import { PAYMENT_MODES, OTHER_ITEM } from './useServiceDetail';
import { isPerUnit } from '../services/useServices';

// Desktop service detail: the engagement's facts, its dynamic lines (menu /
// jobs) and the payment ledger side by side.
export default function ServiceDetailDesktop({ vm }) {
  const {
    service, itemsByGroup, items, payments, loading, itemLabel, itemOptions,
    itemsTotal, paidTotal, canEdit, canMoney,
    itemForm, setItemF, addItem, savingItem, toggleItemStatus, removeItem,
    payForm, setPayF, addPayment, savingPay, removePayment, back,
  } = vm;

  if (loading) return <div className="card card-pad"><Spinner /></div>;
  if (!service) return <div className="card card-pad empty">Service not found. <button className="linklike" onClick={back}>Go back</button></div>;

  return (
    <div>
      <div className="page-head">
        <h1>{service.ServiceType}</h1>
        <span className="muted">{service.CompanyName || service.ContactName || ''}</span>
        <div className="spacer" />
        <button className="btn light" onClick={back}>Back</button>
      </div>

      <div className="card card-pad" style={{ marginBottom: 14 }}>
        <div className="form-grid cols-4">
          <div><div className="muted" style={{ fontSize: 12 }}>Function</div><strong>{service.FunctionName || 'Whole event'}</strong></div>
          <div><div className="muted" style={{ fontSize: 12 }}>Date</div><strong>{fmtDate(service.FunctionDate || service.ServiceDate) || '—'}</strong></div>
          <div><div className="muted" style={{ fontSize: 12 }}>Time</div><strong>
            {fmtTime(service.StartTime || service.FunctionStartTime) || '—'}
            {(service.EndTime || service.FunctionEndTime) ? ` – ${fmtTime(service.EndTime || service.FunctionEndTime)}` : ''}
          </strong></div>
          <div><div className="muted" style={{ fontSize: 12 }}>Venue</div><strong>{[service.Venue, service.City].filter(Boolean).join(', ') || '—'}</strong></div>
          <div><div className="muted" style={{ fontSize: 12 }}>Owner</div><strong>{service.ContactName || service.VendorName || '—'}</strong>
            <div className="sub">{service.ContactMobile || service.VendorMobile || ''}</div></div>
          <div><div className="muted" style={{ fontSize: 12 }}>Rate</div><strong>{money(service.Rate)}</strong>
            {isPerUnit(service.RateType) && <div className="sub">× {service.ConfirmedCount} {service.RateType === 'PerPlate' ? 'plates' : 'units'}</div>}</div>
          <div><div className="muted" style={{ fontSize: 12 }}>Total / paid</div><strong>{money(service.TotalAmount)}</strong>
            <div className="sub">paid {money(service.AdvancePaid)}</div></div>
          <div><div className="muted" style={{ fontSize: 12 }}>Balance</div><strong style={{ color: 'var(--danger)' }}>{money(service.BalanceAmount)}</strong></div>
        </div>
        {service.Notes && <div className="muted" style={{ marginTop: 8, fontSize: 13 }}>{service.Notes}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 14 }}>
        {/* ── The dynamic lines: dishes for a caterer, jobs for a manager ── */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong>{itemLabel} list</strong>
            <span className="muted" style={{ fontSize: 12 }}>{items.length} lines{itemsTotal ? ` · ${money(itemsTotal)}` : ''}</span>
          </div>

          {canEdit && (
            <div className="form-grid cols-4" style={{ alignItems: 'end' }}>
              <div className="span-2">
                <Field label={`Add ${itemLabel.toLowerCase()}`} type="select" value={itemForm.ItemTypeId} options={itemOptions}
                  onChange={(v) => setItemF('ItemTypeId', v)} />
              </div>
              {itemForm.ItemTypeId === OTHER_ITEM && (
                <div className="span-2">
                  <Field label="New name" value={itemForm.ItemName} onChange={(v) => setItemF('ItemName', v)} />
                </div>
              )}
              <Field label="Qty" type="number" value={itemForm.Quantity} onChange={(v) => setItemF('Quantity', v)} />
              <Field label="Rate (₹)" type="number" value={itemForm.Rate} onChange={(v) => setItemF('Rate', v)} />
              <div className="span-all" style={{ textAlign: 'right' }}>
                <button className="btn primary" disabled={savingItem} onClick={addItem}>{savingItem ? 'Adding…' : `+ Add ${itemLabel.toLowerCase()}`}</button>
              </div>
            </div>
          )}

          {items.length === 0 ? (
            <div className="empty" style={{ padding: 16 }}>Nothing added yet.</div>
          ) : itemsByGroup.map(({ group, list }) => (
            <div key={group || 'x'} style={{ marginTop: 10 }}>
              {group && <div className="step-label">{group}</div>}
              <table className="tbl compact">
                <tbody>
                  {list.map(it => (
                    <tr key={it.ServiceItemId}>
                      <td style={{ width: 34 }}>
                        <input type="checkbox" checked={it.Status === 'Done'} disabled={!canEdit}
                          onChange={() => toggleItemStatus(it)} title="Mark done" />
                      </td>
                      <td className="grow" style={{ textDecoration: it.Status === 'Done' ? 'line-through' : 'none' }}>
                        {it.ItemName}{it.Remarks ? <div className="sub">{it.Remarks}</div> : null}
                      </td>
                      <td className="num">{Number(it.Quantity) !== 1 ? it.Quantity : ''}</td>
                      <td className="num">{Number(it.Amount) ? money(it.Amount) : ''}</td>
                      <td style={{ textAlign: 'right' }}>
                        {canEdit && <button className="icon-btn danger" title="Remove" onClick={() => removeItem(it)}>🗑</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* ── Payments ── */}
        <div className="card card-pad">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <strong>Payments</strong>
            <span className="muted" style={{ fontSize: 12 }}>{money(paidTotal)} of {money(service.TotalAmount)}</span>
          </div>
          {canMoney && (
            <div className="form-grid">
              <Field label="Date" type="date" value={payForm.PaymentDate} onChange={(v) => setPayF('PaymentDate', v)} />
              <Field label="Amount (₹)" type="number" value={payForm.Amount} onChange={(v) => setPayF('Amount', v)} />
              <Field label="Mode" type="select" value={payForm.PaymentMode} options={PAYMENT_MODES} onChange={(v) => setPayF('PaymentMode', v)} />
              <Field label="Reference" value={payForm.Reference} onChange={(v) => setPayF('Reference', v)} />
              <div style={{ gridColumn: '1 / -1', textAlign: 'right' }}>
                <button className="btn primary" disabled={savingPay} onClick={addPayment}>{savingPay ? 'Saving…' : '+ Record payment'}</button>
              </div>
            </div>
          )}
          {payments.length === 0 ? (
            <div className="empty" style={{ padding: 16 }}>No payments recorded.</div>
          ) : (
            <table className="tbl compact" style={{ marginTop: 8 }}>
              <tbody>
                {payments.map(p => (
                  <tr key={p.PaymentId}>
                    <td>{fmtDate(p.PaymentDate)}</td>
                    <td className="grow">{p.PaymentMode}{p.Reference ? <div className="sub">{p.Reference}</div> : null}</td>
                    <td className="num"><strong>{money(p.Amount)}</strong></td>
                    <td style={{ textAlign: 'right' }}>
                      {canMoney && <button className="icon-btn danger" title="Remove" onClick={() => removePayment(p)}>🗑</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ marginTop: 10, textAlign: 'right' }}>
            Balance <strong style={{ color: 'var(--danger)' }}>{money(service.BalanceAmount)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
